'use client';

import {
  Background,
  BackgroundVariant,
  ReactFlow,
  ReactFlowProvider,
  type ReactFlowProps,
  type OnNodesChange,
  type OnEdgesChange,
  applyNodeChanges,
  applyEdgeChanges,
  useNodesInitialized,
  useReactFlow,
} from '@xyflow/react';
import { ConnectionLine } from '@/components/connection-line';
import { edgeTypes } from '@/components/edges';
import { nodeTypes } from '@/components/nodes';
import { NodeDropzoneProvider } from '@/providers/node-dropzone';
import { NodeOperationsProvider } from '@/providers/node-operations';
import { MousePointerClick } from 'lucide-react';
import { useI18n } from '@/providers/i18n';
import { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import type { Node, Edge } from '@xyflow/react';

function AutoFitView({ enabled, fitKey }: { enabled: boolean; fitKey: string }) {
  const rf = useReactFlow();
  const nodesInitialized = useNodesInitialized();
  const didFitRef = useRef(false);

  useEffect(() => {
    didFitRef.current = false;
  }, [fitKey]);

  useEffect(() => {
    if (!enabled) return;
    if (!nodesInitialized) return;
    if (didFitRef.current) return;
    didFitRef.current = true;
    // Next tick to ensure viewport + nodes are fully ready
    const id = requestAnimationFrame(() => {
      try {
        rf.fitView({ padding: 0.2, duration: 0, includeHiddenNodes: true });
      } catch {}
    });
    return () => cancelAnimationFrame(id);
  }, [enabled, nodesInitialized, rf, fitKey]);

  return null;
}

export const DemoCanvas = ({
  children,
  hideMiniMap,
  nodes: initialNodes,
  edges: initialEdges,
  forceDark,
  previewMode,
  hideNodeToolbars,
  ...props
}: ReactFlowProps & { hideMiniMap?: boolean; forceDark?: boolean; previewMode?: boolean; hideNodeToolbars?: boolean }) => {
  const {
    panOnDrag: panOnDragProp,
    selectionOnDrag: selectionOnDragProp,
    ...restReactFlowProps
  } = props;

  // Local state for nodes and edges
  const [nodes, setNodes] = useState<Node[]>(initialNodes || []);
  const [edges, setEdges] = useState<Edge[]>(initialEdges || []);
  const { t } = useI18n();
  const fitKey = useMemo(() => {
    const n = Array.isArray(initialNodes) ? initialNodes.length : 0;
    const e = Array.isArray(initialEdges) ? initialEdges.length : 0;
    return `${n}:${e}`;
  }, [initialNodes, initialEdges]);
  const connectionRadius =
    typeof props.connectionRadius === "number" && Number.isFinite(props.connectionRadius)
      ? props.connectionRadius
      : 80;

  // Preload images to avoid loading state issues
  useEffect(() => {
    if (initialNodes) {
      initialNodes.forEach((node: any) => {
        if (node.type === 'image' && node.data.generated?.url) {
          const img = new Image();
          img.src = node.data.generated.url;
        }
        if (node.type === 'image' && node.data.content?.url) {
          const img = new Image();
          img.src = node.data.content.url;
        }
      });
    }
  }, [initialNodes]);

  // Sync when props change (enable switching workflows)
  useEffect(() => {
    setNodes(initialNodes || []);
  }, [initialNodes]);

  useEffect(() => {
    setEdges(initialEdges || []);
  }, [initialEdges]);

  // Handle node changes (including drag)
  const handleNodesChange = useCallback<OnNodesChange>(
    (changes) => {
      setNodes((current) => applyNodeChanges(changes, current));
    },
    []
  );

  // Handle edge changes
  const handleEdgesChange = useCallback<OnEdgesChange>(
    (changes) => {
      setEdges((current) => applyEdgeChanges(changes, current));
    },
    []
  );

  // Mock functions for NodeOperationsProvider
  const addNode = useCallback((type: string, options?: Record<string, unknown>) => {
    // No-op for demo, return empty string
    return '';
  }, []);

  const duplicateNode = useCallback((id: string) => {
    // No-op for demo
  }, []);

  const cloneNode = useCallback((id: string) => {
    // No-op for demo
  }, []);

  return (
    <ReactFlowProvider>
      <NodeOperationsProvider addNode={addNode} duplicateNode={duplicateNode} cloneNode={cloneNode}>
        <NodeDropzoneProvider>
          <div
            className={`relative w-full h-full ${forceDark ? 'dark' : ''}`}
            data-preview-canvas={(previewMode || hideNodeToolbars) ? 'true' : undefined}
          >
            <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={handleNodesChange}
            onEdgesChange={handleEdgesChange}
            deleteKeyCode={['Backspace', 'Delete']}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            connectionLineComponent={ConnectionLine}
            connectionRadius={connectionRadius}
            minZoom={0.05}
            zoomOnScroll={true}
            panOnScroll={true}
            zoomActivationKeyCode="Meta"
            zoomOnDoubleClick={false}
            // Allow middle-mouse drag panning by default (callers can override via props).
            panOnDrag={panOnDragProp ?? [1]}
            selectionOnDrag={selectionOnDragProp ?? true}
            proOptions={{ hideAttribution: true }}
            {...restReactFlowProps}
            >
              <AutoFitView enabled={Boolean(previewMode)} fitKey={fitKey} />
              <Background
                variant={BackgroundVariant.Dots}
                gap={30}
                size={1.5}
                color={'var(--canvas-dot)'}
                bgColor={'var(--canvas-bg)'}
              />
              {children}
            </ReactFlow>
            {/* Empty state hint */}
            {nodes.length === 0 && edges.length === 0 && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center z-30 text-sm text-gray-500">
                <span className="inline-flex items-center gap-2">
                  <MousePointerClick size={24} className="opacity-80" />
                  {t('Double-click the canvas or use the toolbar to create a node')}
                </span>
              </div>
            )}
          </div>
        </NodeDropzoneProvider>
      </NodeOperationsProvider>
    </ReactFlowProvider>
  );
};
